/* 
██╗██╗░░░██╗  ██████╗░██╗░██████╗░██╗████████╗░█████╗░██╗░░░░░
██║██║░░░██║  ██╔══██╗██║██╔════╝░██║╚══██╔══╝██╔══██╗██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░██╗░██║░░░██║░░░███████║██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░╚██╗██║░░░██║░░░██╔══██║██║░░░░░
██║╚██████╔╝  ██████╔╝██║╚██████╔╝██║░░░██║░░░██║░░██║███████╗
╚═╝░╚═════╝░  ╚═════╝░╚═╝░╚═════╝░╚═╝░░░╚═╝░░░╚═╝░░╚═╝╚══════╝
╔════════════════════════════════════════════════════════════╗
║              EQUIPO DE DESARROLLO DE SOFTWARE              ║
║                   MODULO DESARROLLADO POR                  ║
╟┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄╢
║ Cristia David Lemus Calderón                               ║
║ cristian.lemus@iudigital.edu.co                            ║
╚════════════════════════════════════════════════════════════╝
*/

/**
 * Controlador para verificación de carnets por ID.
 *
 * Este controlador maneja la lógica de verificación de carnets mediante códigos QR,
 * incluyendo validación de tipos de carnet, autenticación condicional y gestión
 * de estados de ingreso para diferentes tipos de usuarios.
 *
 * @author eulicer zapata <eulicer.zapata@iudigital.edu.co>
 * @date [Fecha de creación: 2025-09-15]
 * @updated [Fecha de última modificación: 2025-09-15]
 * @updatedBy eulicer zapata <eulicer.zapata@iudigital.edu.co>
 */

import { useEffect, useState } from "react";
import {
  getCardType,
  getCardPerId,
  getPublicCardData,
  updatedJoinCarnet,
} from "../services/VerifyCarnetIdService";
import VerifyById from "../modules/VerifyById";
import React from "react";
import MDAlert from "components/Alert";

/**
 * Componente controlador principal para verificación de carnets por ID.
 * Maneja la lógica de verificación de carnets mediante códigos QR,
 * incluyendo validación de tipos, autenticación condicional y gestión de estados.
 * @param {boolean} isAuthenticated - Indica si el usuario está autenticado.
 * @returns {JSX.Element} Componente de React con la interfaz de verificación.
 */
export default function VerifyCarnetIDController({ isAuthenticated = false }) {
  const [data, setData] = useState([]);
  const [urlAfterVerify, seturlAfterVerify] = useState([]);
  const [isAlert, setIsAlert] = useState(false);
  const [typeAlert, setTypeAlert] = useState("");
  const [message, setMessage] = useState("");
  const [needsAuth, setNeedsAuth] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialJoinedState, setInitialJoinedState] = useState(null);
  const [joinedStateApplicated, setJoinedStateApplicated] = useState(null);

  /**
   * Extrae el valor de la URL desde la posición 2 después de /verify/
   * @returns {string} El valor extraído de la URL
   */
  const getUrlValue = () => {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    const pathParts = url.pathname.split("/").filter((part) => part !== "");
    return pathParts[2]; // Extrae "false" de la posición 2
  };

  /**
   * Función principal para procesar y verificar un carnet mediante QR.
   * Extrae el ID del carnet de la URL, verifica el tipo de carnet,
   * determina si requiere autenticación y obtiene los datos correspondientes.
   * @param {Event} e - Evento que dispara la función (opcional).
   */
  const request = async (isAction = false) => {
    setIsLoading(true);
    //se captura el id de carnet en la url
    const currentUrl = window.location.href;

    try {
      // Primero verificar el tipo de carnet (sin autenticación)
      const cardTypeResult = await getCardType(currentUrl);

      if (cardTypeResult.status === false) {
        // Si el error indica que requiere autenticación, usar endpoint privado
        if (cardTypeResult.requiresAuth || cardTypeResult.usePrivateEndpoint) {
          // Extraer parámetros de la URL para usar endpoint privado
          const url = new URL(currentUrl);
          const pathParts = url.pathname.split("/").filter((part) => part !== "");

          if (pathParts.length >= 3 && pathParts[0] === "verify") {
            const id = pathParts[1];
            const qr = pathParts[2];

            // Usar endpoint privado directamente
            const result = await getCardPerId(id, qr);

            if (result.status !== false) {
              console.log("result", result.payload);
              await setData(result.payload);
              await seturlAfterVerify(id);
              setInitialJoinedState(result.payload.joined);
              setIsLoading(false);
              return;
            } else {
              handleAlert("fail", result.message || "Error del servidor al hacer la solicitud");
              setIsLoading(false);
              return;
            }
          }
        }

        handleAlert("fail", cardTypeResult.message || "Error al verificar el carnet");
        setIsLoading(false);
        return;
      }

      const { cardType, isValid, id, qr, message: apiMessage } = cardTypeResult;

      if (!isValid) {
        handleAlert("fail", apiMessage || "Carnet no válido");
        setIsLoading(false);
        return;
      }

      // Si el usuario está autenticado, siempre usar endpoint privado
      // Si no está autenticado, usar endpoint público (excepto para Invitado/Visitante)
      let result;
      if (isAuthenticated) {
        // Usuario autenticado: siempre usar endpoint privado
        result = await getCardPerId(id, qr, isAction);
      } else {
        // Usuario no autenticado con otros tipos de carnet: usar endpoint público
        result = await getPublicCardData(id, qr);
      }

      if (result.status !== false) {
        console.log("result", result.payload);
        await setData(result.payload);
        await seturlAfterVerify(id);
        setInitialJoinedState(result.payload.joined);
        setIsLoading(false);
      } else {
        handleAlert("fail", result.message || "Error del servidor al hacer la solicitud");
        setIsLoading(false);
      }
    } catch (error) {
      handleAlert("fail", "Error al obtener los datos del carnet.");
      setIsLoading(false);
    }
  };

  /**
   * Maneja el registro de entrada del carnet.
   * Actualiza el estado del carnet marcando una entrada.
   */
  const handleEntryAction = async () => {
    const data = {
      status: "Activo",
      action_type: "entry",
      qr: getUrlValue(),
    };

    try {
      const result = await updatedJoinCarnet(urlAfterVerify, data);
      if (result.status !== false) {
        handleAlert("success", "Entrada registrada correctamente");
        setJoinedStateApplicated("1");
        request(true);
      } else {
        handleAlert("fail", result.message || "Error al registrar entrada");
      }
    } catch (error) {
      handleAlert("fail", "Error al registrar entrada");
    }
  };

  /**
   * Maneja el registro de salida del carnet.
   * Actualiza el estado del carnet marcando una salida.
   */
  const handleExitAction = async () => {
    const data = {
      status: "Activo",
      action_type: "exit",
      qr: getUrlValue(),
    };

    try {
      const result = await updatedJoinCarnet(urlAfterVerify, data);
      if (result.status !== false) {
        handleAlert("success", "Salida registrada correctamente");
        setJoinedStateApplicated("0");
        request(true);
      } else {
        handleAlert("fail", result.message || "Error al registrar salida");
      }
    } catch (error) {
      handleAlert("fail", error.message || "Error al registrar salida");
    }
  };

  useEffect(() => {
    // Obtener la URL actual
    request();
  }, []); // Se ejecutará solo una vez al montar el componente

  /**
   * Maneja la activación y configuración de alertas en la interfaz.
   * Establece el tipo de alerta, mensaje y activa la visualización
   * de notificaciones para el usuario.
   * @param {string} typeAlert - Tipo de alerta ("success" o "fail").
   * @param {string} message - Mensaje a mostrar en la alerta.
   */
  const handleAlert = (typeAlert, message) => {
    setTypeAlert(typeAlert);
    setMessage(message);
    setIsAlert(true);
  };

  if (needsAuth) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h3>Redirigiendo al login...</h3>
        <p>Este carnet requiere autenticación para ser visualizado.</p>
      </div>
    );
  }

  return (
    <>
      {/* Solo mostrar MDAlert en vista autenticada */}
      {isAuthenticated && (
        <MDAlert
          isAlert={isAlert}
          setIsAlert={setIsAlert}
          typeAlert={typeAlert}
          message={message}
        ></MDAlert>
      )}

      <VerifyById
        data={data}
        onEntryAction={handleEntryAction}
        onExitAction={handleExitAction}
        isAuthenticated={isAuthenticated}
        controllerMessage={message}
        alertType={typeAlert}
        isLoading={isLoading}
        initialJoinedState={initialJoinedState}
        joinedStateApplicated={joinedStateApplicated}
      ></VerifyById>
    </>
  );
}
